import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// 5 before/after points — passed in from Impact.js
const beforeAfter = [
  { before: 'Average income ₹3–7K/month, irregular', after: 'Can generate ₹10–18K steady income/month' },
  { before: 'No specific skill set, only menial labour', after: 'End-to-end skill development in FRP production' },
  { before: 'Limited understanding of pricing and product value', after: 'Confident with pricing, margins, and product value' },
  { before: 'No exposure to markets or customers', after: 'Confidently interact with customers and drive sales' },
  { before: "Dependent on spouse's salary to run household", after: 'Financially independent in household decisions' }
];

// Animated single row — fades in during its own scroll slice
const PointRow = ({ scrollYProgress, index, before, after }) => {
  // Spread 5 points evenly across the "active" portion of scroll (10% → 65%)
  // so point 5 finishes revealing well before the section ends.
  const start = 0.10 + index * 0.09;
  const end = start + 0.12;
  const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);
  const x = useTransform(scrollYProgress, [start, end], [-24, 0]);
  const xRight = useTransform(scrollYProgress, [start, end], [24, 0]);

  return (
    <>
      {/* Left — problem */}
      <motion.div style={{ opacity, x }} className="flex items-start gap-3 md:gap-4">
        <span className="text-[11px] md:text-sm text-[#7a5e75] font-medium pt-1 tabular-nums shrink-0">
          0{index + 1}
        </span>
        <p className="text-xs md:text-base text-white/70 leading-relaxed">{before}</p>
      </motion.div>

      {/* Right — potential */}
      <motion.div style={{ opacity, x: xRight }} className="flex items-start gap-3 md:gap-4 justify-end text-right">
        <p className="text-xs md:text-base text-white font-light leading-relaxed">{after}</p>
        <span className="text-[11px] md:text-sm text-[#bc64aa] font-medium pt-1 tabular-nums shrink-0">
          0{index + 1}
        </span>
      </motion.div>
    </>
  );
};

const ProblemPotential = ({ image }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  // Face scale: grows gradually across the section, most zoomed in near the end
  const faceScale = useTransform(scrollYProgress, [0, 0.5, 0.95], [0.85, 1.05, 1.2]);
  // Intro header fades out quickly
  const introOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  // Sticky column labels fade in once user enters section
  const headerOpacity = useTransform(scrollYProgress, [0.05, 0.15], [0, 1]);

  return (
    <section
      ref={containerRef}
      data-testid="impact-problem-potential"
      className="relative bg-[#641b4d]"
      // Tall container so all 5 point rows have scroll room to reveal
      style={{ height: '400vh' }}
    >
      {/* Sticky viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col">
        {/* Intro header — appears on first frame, fades out as user scrolls */}
        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute inset-x-0 top-0 px-6 pt-28 text-center z-20"
        >
          <p className="text-[11px] md:text-sm uppercase tracking-[0.3em] text-[#FFD454] font-semibold">
            The Story Behind Every Planter
          </p>
        </motion.div>

        {/* Column headers */}
        <motion.div
          style={{ opacity: headerOpacity }}
          className="absolute inset-x-0 top-28 md:top-32 z-10 grid grid-cols-2 max-w-6xl mx-auto w-full px-6 md:px-12"
        >
          <div className="flex">
            <span className="inline-block bg-[#7a5e75]/80 text-white text-[10px] md:text-xs uppercase tracking-[0.2em] px-3 py-1.5 backdrop-blur-sm">
              The Problem
            </span>
          </div>
          <div className="flex justify-end">
            <span className="inline-block bg-[#bc64aa] text-white text-[10px] md:text-xs uppercase tracking-[0.2em] px-3 py-1.5">
              The Potential
            </span>
          </div>
        </motion.div>

        {/* Center face — circular cutout with dotted border, centered on viewport */}
        <motion.div
          style={{ scale: faceScale }}
          className="absolute left-1/2 top-1/2 z-0 will-change-transform w-[340px] h-[340px] md:w-[480px] md:h-[480px] lg:w-[580px] lg:h-[580px]"
          // -50% translate keeps center of this wrapper pinned to viewport center
          initial={{ x: '-50%', y: '-50%' }}
        >
          {/* Outer dotted ring — rotating subtly */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 90, ease: 'linear', repeat: Infinity }}
            className="absolute inset-0 w-full h-full rounded-full border border-dashed border-white/25"
          />
          {/* Second dotted ring — slightly inset, counter-rotating */}
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 120, ease: 'linear', repeat: Infinity }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[86%] h-[86%] rounded-full border border-dotted border-white/10"
          />
          {/* Inner face disc — her face fills the circle cleanly.
              The circular crop + green border ring already frame her nicely;
              no overlay needed so her face stays fully visible. */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full overflow-hidden bg-[#641b4d] shadow-[0_25px_60px_rgba(0,0,0,0.35)]"
          >
            <img
              src={image}
              alt="The woman behind every Veveru planter"
              className="w-full h-full object-cover"
              style={{
                // Focus on her face (upper portion of original photo)
                objectPosition: '50% 22%'
              }}
            />
            {/* Subtle edge feather — only at the very edge of the circle */}
            <div className="absolute inset-0 pointer-events-none rounded-full" style={{
              boxShadow: 'inset 0 0 40px 12px rgba(26,46,37,0.4)'
            }} />
          </div>
        </motion.div>

        {/* Before/After point rows — fixed layout on left/right, staggered reveal */}
        <div className="relative z-10 flex-1 flex items-center px-6 md:px-12">
          <div className="max-w-6xl mx-auto w-full grid grid-cols-2 gap-x-6 md:gap-x-12 lg:gap-x-24 gap-y-6 md:gap-y-10 pointer-events-none">
            {beforeAfter.map((row, i) => (
              <PointRow
                key={i}
                scrollYProgress={scrollYProgress}
                index={i}
                before={row.before}
                after={row.after}
              />
            ))}
          </div>
        </div>

        {/* Scroll hint — bottom center, fades out */}
        <motion.div
          style={{ opacity: useTransform(scrollYProgress, [0, 0.08], [1, 0]) }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemPotential;
